import React, { useState, useMemo, useEffect, useRef } from "react";
import { Card, Table, Row, Col, Form, InputGroup, Button, Pagination, Dropdown, Badge, Modal, Spinner, Alert, Toast, ToastContainer, OverlayTrigger, Tooltip } from "react-bootstrap";
import jsPDF from "jspdf";
import * as XLSX from "xlsx";
import { secureGroupApi, groupeCouleurs } from "../services/secureApi";
import Footer from "../layouts/Footer";

export default function GroupManagement() {
  const [groupesDisponibles, setGroupesDisponibles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterText, setFilterText] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);
  const [sortField, setSortField] = useState('created_at');
  const [sortDirection, setSortDirection] = useState('desc');
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [exportLoading, setExportLoading] = useState(false);
  const [showCreateGroupModal, setShowCreateGroupModal] = useState(false);
  const [showEditGroupModal, setShowEditGroupModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [groupDetail, setGroupDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastVariant, setToastVariant] = useState('success');

  const [groupForm, setGroupForm] = useState({
    name: '',
    couleur: 'primary'
  });

  const initDoneRef = useRef(false);

  // ============================================
  // TOASTS
  // ============================================
  const showAlert = (message, variant = 'success') => {
    setToastMessage(message);
    setToastVariant(variant);
    setShowToast(true);
  };

  const getToastIcon = (variant) => {
    switch (variant) {
      case 'success': return 'bi-check-circle-fill';
      case 'danger': return 'bi-exclamation-circle-fill';
      case 'warning': return 'bi-exclamation-triangle-fill';
      case 'info': return 'bi-info-circle-fill';
      default: return 'bi-bell-fill';
    }
  };

  // ============================================
  // CHARGEMENT DES GROUPES
  // ============================================
  const fetchGroupes = async () => {
    try {
      setLoading(true);
      setError(null);

      const result = await secureGroupApi.fetchGroups();

      if (result.code === 200) {
        const groupesTransformes = result.data.map(groupe => ({
          id: groupe.id,
          nom: groupe.name,
          business_name: groupe.business_name,
          name: groupe.name,
          couleur: groupeCouleurs[groupe.id % groupeCouleurs.length].value,
          description: groupe.description || `Groupe créé le ${new Date(groupe.created_at).toLocaleDateString('fr-FR')}`,
          dateCreation: new Date(groupe.created_at).toLocaleDateString('fr-FR'),
          created_at: groupe.created_at,
          updated_at: groupe.updated_at,
          user_id: groupe.user_id,
          entite_id: groupe.entite_id,
          contactsCount: groupe.contacts_count || 0
        }));

        setGroupesDisponibles(groupesTransformes);
      }
    } catch (err) {
      console.error('❌ Erreur groupes:', err);
      // setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchGroupDetail = async (groupId) => {
    try {
      setDetailLoading(true);
      const result = await secureGroupApi.fetchGroupDetail(groupId);

      if (result.code === 200) {
        setGroupDetail(result.data);
        return { success: true, data: result.data };
      } else {
        throw new Error(result.message || 'Erreur lors du chargement');
      }
    } catch (err) {
      console.error('❌ Erreur détails:', err);
      showAlert('Erreur lors du chargement des détails', 'danger');
      return { success: false, message: err.message };
    } finally {
      setDetailLoading(false);
    }
  };

  useEffect(() => {
    if (initDoneRef.current) return;
    initDoneRef.current = true;
    fetchGroupes();
  }, []);

  // ============================================
  // CRUD
  // ============================================
  const createGroup = async (groupData) => {
    try {
      const result = await secureGroupApi.createGroup(groupData);

      if (result.code === 200 || result.code === 201) {
        return { success: true, message: "Groupe créé avec succès" };
      } else {
        throw new Error(result.message || 'Erreur lors de la création');
      }
    } catch (err) {
      console.error('❌ Erreur création:', err);
      return { success: false, message: err.message };
    }
  };

  const updateGroup = async (groupId, groupData) => {
    try {
      const result = await secureGroupApi.updateGroup(groupId, groupData);

      if (result.code === 200) {
        return { success: true, message: "Groupe mis à jour avec succès" };
      } else {
        throw new Error(result.message || 'Erreur lors de la mise à jour');
      }
    } catch (err) {
      console.error('❌ Erreur mise à jour:', err);
      return { success: false, message: err.message };
    }
  };

  const deleteGroup = async (groupId) => {
    try {
      const result = await secureGroupApi.deleteGroup(groupId);

      if (result.code === 200) {
        return { success: true, message: "Groupe supprimé avec succès" };
      } else {
        throw new Error(result.message || 'Erreur lors de la suppression');
      }
    } catch (err) {
      console.error('❌ Erreur suppression:', err);
      return { success: false, message: err.message };
    }
  };

  // ============================================
  // HANDLERS
  // ============================================
  const handleViewDetails = async (group) => {
    setSelectedGroup(group);
    setShowDetailModal(true);
    await fetchGroupDetail(group.id);
  };

  const handleCreateGroup = async () => {
    if (!groupForm.name.trim()) {
      showAlert('Veuillez saisir un nom pour le groupe', 'warning');
      return;
    }

    setActionLoading(true);
    try {
      const result = await createGroup(groupForm);

      if (result.success) {
        showAlert(result.message, 'success');
        setGroupForm({ name: '', couleur: 'primary' });
        setShowCreateGroupModal(false);
        await fetchGroupes();
      } else {
        showAlert(`Erreur: ${result.message}`, 'danger');
      }
    } finally {
      setActionLoading(false);
    }
  };

  const handleEditGroup = (group) => {
    setSelectedGroup(group);
    setGroupForm({
      name: group.name,
      couleur: group.couleur
    });
    setShowEditGroupModal(true);
  };

  const handleUpdateGroup = async () => {
    if (!groupForm.name.trim()) {
      showAlert('Veuillez saisir un nom pour le groupe', 'warning');
      return;
    }

    setActionLoading(true);
    try {
      const result = await updateGroup(selectedGroup.id, groupForm);

      if (result.success) {
        showAlert(result.message, 'success');
        setShowEditGroupModal(false);
        setSelectedGroup(null);
        setGroupForm({ name: '', couleur: 'primary' });
        await fetchGroupes();
      } else {
        showAlert(`Erreur: ${result.message}`, 'danger');
      }
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteClick = (group) => {
    setSelectedGroup(group);
    setSelectedGroups([]);
    setShowDeleteModal(true);
  };

const handleConfirmDelete = async () => {
  if (!selectedGroup) return;

  const groupIdToDelete = selectedGroup.id;
  const groupNameToDelete = selectedGroup.nom;

  setActionLoading(true);
  try {
    const result = await deleteGroup(groupIdToDelete);
    console.log('📊 Résultat delete:', result);

    if (result.success) {
      // ✅ OPTIMISTE : Supprimer localement IMMÉDIATEMENT
      setGroupesDisponibles(prev => prev.filter(g => g.id !== groupIdToDelete));
      
      showAlert(`Groupe "${groupNameToDelete}" supprimé`, 'success');
      
      // ✅ Fermer le modal TOUT DE SUITE
      setShowDeleteModal(false);
      setSelectedGroup(null);
      setSelectedGroups([]);
      
      // ✅ Actualiser en arrière-plan (sans bloquer l'UI)
      fetchGroupes();
    } else {
      showAlert(`Erreur: ${result.message}`, 'danger');
      setShowDeleteModal(false);
      setSelectedGroup(null);
    }
  } catch (err) {
    console.error('❌ Erreur:', err);
    showAlert(`Erreur: ${err.message}`, 'danger');
  } finally {
    setActionLoading(false);
  }
};

  const handleConfirmBulkDelete = async () => {
    if (selectedGroups.length === 0) return;

    const totalToDelete = selectedGroups.length;
    const groupsToDelete = [...selectedGroups];

    setActionLoading(true);
    try {
      // ✅ Supprimer tous les groupes sélectionnés
      const results = await Promise.allSettled(
        groupsToDelete.map(groupId => deleteGroup(groupId))
      );

      const successCount = results.filter(
        r => r.status === 'fulfilled' && r.value?.success
      ).length;

      // ✅ Actualiser la liste complète après suppression
      await fetchGroupes();

      // ✅ Réinitialiser les sélections et la page
      setSelectedGroups([]);
      setCurrentPage(1);

      if (successCount === totalToDelete) {
        showAlert(`${successCount} groupe(s) supprimé(s) avec succès`, 'success');
      } else if (successCount > 0) {
        showAlert(
          `${successCount} groupe(s) supprimé(s), ${totalToDelete - successCount} échec(s)`,
          'warning'
        );
      } else {
        showAlert('Aucun groupe n\'a pu être supprimé', 'danger');
      }
    } catch (err) {
      console.error('❌ Erreur suppression multiple:', err);
      showAlert(`Erreur: ${err.message}`, 'danger');
    } finally {
      setActionLoading(false);
      setShowDeleteModal(false);
      setSelectedGroup(null);
    }
  };

  // ============================================
  // FILTRAGE ET TRI
  // ============================================
  const filteredAndSortedGroups = useMemo(() => {
    let filtered = groupesDisponibles.filter(groupe =>
      groupe.nom.toLowerCase().includes(filterText.toLowerCase()) ||
      groupe.business_name.toLowerCase().includes(filterText.toLowerCase()) ||
      (groupe.description && groupe.description.toLowerCase().includes(filterText.toLowerCase()))
    );

    filtered.sort((a, b) => {
      let aValue = a[sortField];
      let bValue = b[sortField];

      if (sortField === 'dateCreation') {
        aValue = new Date(a.created_at);
        bValue = new Date(b.created_at);
      }

      if (sortDirection === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    return filtered;
  }, [groupesDisponibles, filterText, sortField, sortDirection]);

  // ============================================
  // STATS
  // ============================================
  const stats = useMemo(() => {
    const totalGroups = groupesDisponibles.length;
    const totalContacts = groupesDisponibles.reduce((sum, groupe) => sum + groupe.contactsCount, 0);
    const averageContacts = totalGroups > 0 ? Math.round(totalContacts / totalGroups) : 0;

    return { totalGroups, totalContacts, averageContacts };
  }, [groupesDisponibles]);

  // ============================================
  // PAGINATION
  // ============================================
  const totalPages = Math.ceil(filteredAndSortedGroups.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentGroups = filteredAndSortedGroups.slice(startIndex, startIndex + itemsPerPage);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // ============================================
  // SÉLECTION
  // ============================================
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedGroups(currentGroups.map(group => group.id));
    } else {
      setSelectedGroups([]);
    }
  };

  const handleSelectGroup = (groupId) => {
    setSelectedGroups(prev =>
      prev.includes(groupId)
        ? prev.filter(id => id !== groupId)
        : [...prev, groupId]
    );
  };

  const isAllSelected = currentGroups.length > 0 && selectedGroups.length === currentGroups.length;
  const isSomeSelected = selectedGroups.length > 0 && selectedGroups.length < currentGroups.length;

  // ============================================
  // EXPORT
  // ============================================
  const handleExport = (format) => {
    setExportLoading(true);
    const dataToExport = selectedGroups.length > 0
      ? groupesDisponibles.filter(group => selectedGroups.includes(group.id))
      : filteredAndSortedGroups;

    if (!dataToExport.length) {
      showAlert('Aucune donnée à exporter', 'warning');
      setExportLoading(false);
      return;
    }

    try {
      if (format === "csv") {
        const headers = ["Nom", "Description", "Date Création"];
        const csvContent = [
          "\uFEFF" + headers.join(";"),
          ...dataToExport.map(g => [
            g.nom, g.description, g.dateCreation
          ].map(v => `"${v}"`).join(";"))
        ].join("\n");
        downloadFile(csvContent, "text/csv;charset=utf-8;", `export-groupes-${Date.now()}.csv`);
      } else if (format === "excel") {
        const ws = XLSX.utils.json_to_sheet(dataToExport.map(g => ({
          Nom: g.nom, Description: g.description, "Date Création": g.dateCreation
        })));
        ws['!cols'] = [{ wch: 20 }, { wch: 30 }, { wch: 15 }];
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Groupes");
        XLSX.writeFile(wb, `export-groupes-${Date.now()}.xlsx`);
      } else if (format === "json") {
        downloadFile(JSON.stringify(dataToExport, null, 2), "application/json;charset=utf-8;", `export-groupes-${Date.now()}.json`);
      } else if (format === "pdf") {
        generatePDF(dataToExport);
      }

      showAlert(`Export ${format.toUpperCase()} réalisé avec succès`, 'success');
    } catch (error) {
      showAlert(`Erreur lors de l'export: ${error.message}`, 'danger');
    } finally {
      setExportLoading(false);
    }
  };

  const generatePDF = (data) => {
    const doc = new jsPDF();
    const primaryColor = [41, 128, 185];
    const headerColor = [240, 240, 240];
    const alternateColor = [248, 249, 250];

    doc.setFillColor(...primaryColor);
    doc.rect(0, 0, 210, 25, 'F');

    doc.setTextColor(255, 255, 255);
    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.text("GESTION DES GROUPES", 105, 15, { align: 'center' });

    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    doc.text(`Export généré le: ${new Date().toLocaleDateString('fr-FR')}`, 15, 22);
    doc.text(`Total: ${data.length} groupe(s)`, 180, 22, { align: 'right' });

    const pageWidth = 210;
    const margin = 10;
    let yPosition = 35;

    const colWidths = [60, 80, 40, 40];
    const headers = ["NOM", "DESCRIPTION", "ENTITE", "DATE CRÉATION"];

    doc.setFillColor(...headerColor);
    doc.rect(margin, yPosition - 4, pageWidth - 2 * margin, 8, 'F');

    doc.setTextColor(0, 0, 0);
    doc.setFontSize(9);
    doc.setFont("helvetica", "bold");

    let xPosition = margin;
    headers.forEach((header, index) => {
      doc.text(header, xPosition + 2, yPosition);
      xPosition += colWidths[index];
    });

    yPosition += 6;
    doc.setFontSize(8);
    doc.setFont("helvetica", "normal");

    data.forEach((item, index) => {
      if (yPosition > 270) {
        doc.addPage();
        yPosition = 20;
        doc.setFillColor(...headerColor);
        doc.rect(margin, yPosition - 4, pageWidth - 2 * margin, 8, 'F');
        doc.setTextColor(0, 0, 0);
        doc.setFontSize(9);
        doc.setFont("helvetica", "bold");
        let newX = margin;
        headers.forEach((header, idx) => {
          doc.text(header, newX + 2, yPosition);
          newX += colWidths[idx];
        });
        yPosition += 6;
        doc.setFontSize(8);
        doc.setFont("helvetica", "normal");
      }

      if (index % 2 === 0) {
        doc.setFillColor(...alternateColor);
        doc.rect(margin, yPosition - 4, pageWidth - 2 * margin, 6, 'F');
      }

      doc.setTextColor(0, 0, 0);
      const rowData = [
        item.nom,
        item.description.length > 50 ? item.description.substring(0, 50) + '...' : item.description,
        item.business_name,
        item.dateCreation
      ];

      let currentX = margin;
      rowData.forEach((cell, cellIndex) => {
        doc.text(cell, currentX + 2, yPosition);
        currentX += colWidths[cellIndex];
      });

      yPosition += 6;
    });

    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setDrawColor(200, 200, 200);
      doc.line(margin, 275, pageWidth - margin, 275);
      doc.setTextColor(100, 100, 100);
      doc.setFontSize(8);
      doc.setFont("helvetica", "normal");
      doc.text(`Page ${i} sur ${pageCount}`, 105, 282, { align: 'center' });
    }

    const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
    doc.save(`groupes-${timestamp}.pdf`);
  };

  const downloadFile = (content, type, filename) => {
    const blob = new Blob([content], { type });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.click();
    setTimeout(() => URL.revokeObjectURL(link.href), 100);
  };

  // ============================================
  // SUB-COMPONENTS
  // ============================================
  const SortableHeader = ({ field, children }) => (
    <th className="gm-sortable-header" onClick={() => handleSort(field)}>
      <span className="gm-sortable-content">
        {children}
        {sortField === field && (
          <i className={`bi bi-caret-${sortDirection === 'asc' ? 'up' : 'down'}-fill gm-sort-icon`}></i>
        )}
      </span>
    </th>
  );

  return (
    <div className="gm-page main main-app p-3 p-lg-4">
      {/* ========== TOASTS ========== */}
      <ToastContainer position="top-end" className="p-3" style={{ zIndex: 9999 }}>
        <Toast
          show={showToast}
          onClose={() => setShowToast(false)}
          delay={5000}
          autohide
          bg={toastVariant}
        >
          <Toast.Header className={`bg-${toastVariant} text-white`}>
            <i className={`bi ${getToastIcon(toastVariant)} me-2`}></i>
            <strong className="me-auto">
              {toastVariant === 'success' ? 'Succès' :
               toastVariant === 'danger' ? 'Erreur' :
               toastVariant === 'warning' ? 'Attention' : 'Information'}
            </strong>
            <small>Maintenant</small>
          </Toast.Header>
          <Toast.Body className="text-white">{toastMessage}</Toast.Body>
        </Toast>
      </ToastContainer>

      {/* ========== HEADER ========== */}
      <div className="gm-header mb-4">
        <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
          <div className="d-flex align-items-center gap-3">
            <div className="gm-header-icon">
              <i className="bi bi-collection-fill"></i>
            </div>
            <div>
              <h3 className="gm-header-title mb-1">Gestion des groupes</h3>
              <p className="gm-header-subtitle mb-0">
                Organisez et gérez vos groupes de contacts
              </p>
            </div>
          </div>
          <div className="d-flex flex-wrap gap-2">
            {selectedGroups.length > 0 && (
              <Button
                className="gm-btn-danger"
                onClick={() => {
                  setSelectedGroup(null);
                  setShowDeleteModal(true);
                }}
                disabled={actionLoading}
              >
                <i className="bi bi-trash"></i>
                <span>Supprimer ({selectedGroups.length})</span>
              </Button>
            )}

            <Dropdown>
              <Dropdown.Toggle className="gm-btn-secondary" disabled={exportLoading}>
                <i className={`bi ${exportLoading ? 'bi-arrow-repeat gm-spin' : 'bi-download'}`}></i>
                <span>Exporter</span>
              </Dropdown.Toggle>
              <Dropdown.Menu className="gm-dropdown-menu">
                <Dropdown.Item onClick={() => handleExport('csv')}>
                  <i className="bi bi-file-earmark-spreadsheet text-success me-2"></i>Export CSV
                </Dropdown.Item>
                <Dropdown.Item onClick={() => handleExport('excel')}>
                  <i className="bi bi-file-earmark-excel text-success me-2"></i>Export Excel
                </Dropdown.Item>
                <Dropdown.Item onClick={() => handleExport('pdf')}>
                  <i className="bi bi-file-earmark-pdf text-danger me-2"></i>Export PDF
                </Dropdown.Item>
                <Dropdown.Item onClick={() => handleExport('json')}>
                  <i className="bi bi-file-earmark-code text-warning me-2"></i>Export JSON
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>

            <Button
              className="gm-btn-primary"
              onClick={() => setShowCreateGroupModal(true)}
              disabled={actionLoading}
            >
              <i className="bi bi-plus-circle"></i>
              <span>Nouveau groupe</span>
            </Button>
          </div>
        </div>
      </div>

      {/* ========== ERROR ========== */}
      {error && (
        <div className="gm-alert gm-alert-danger mb-4">
          <i className="bi bi-exclamation-triangle-fill"></i>
          <div className="flex-grow-1">{error}</div>
        </div>
      )}

      {/* ========== STATS ========== */}
      <Row className="g-3 mb-4">
        <Col sm={6} lg={4}>
          <div className="gm-stat-card gm-stat-blue">
            <div className="gm-stat-icon-wrapper gm-stat-icon-blue">
              <i className="bi bi-collection-fill"></i>
            </div>
            <div className="gm-stat-content">
              <div className="gm-stat-label">Total groupes</div>
              <div className="gm-stat-value">{stats.totalGroups.toLocaleString('fr-FR')}</div>
            </div>
          </div>
        </Col>
        <Col sm={6} lg={4}>
          <div className="gm-stat-card gm-stat-green">
            <div className="gm-stat-icon-wrapper gm-stat-icon-green">
              <i className="bi bi-people-fill"></i>
            </div>
            <div className="gm-stat-content">
              <div className="gm-stat-label">Total contacts</div>
              <div className="gm-stat-value">{stats.totalContacts.toLocaleString('fr-FR')}</div>
            </div>
          </div>
        </Col>
        <Col sm={12} lg={4}>
          <div className="gm-stat-card gm-stat-violet">
            <div className="gm-stat-icon-wrapper gm-stat-icon-violet">
              <i className="bi bi-graph-up-arrow"></i>
            </div>
            <div className="gm-stat-content">
              <div className="gm-stat-label">Moyenne / groupe</div>
              <div className="gm-stat-value">{stats.averageContacts.toLocaleString('fr-FR')}</div>
            </div>
          </div>
        </Col>
      </Row>

      {/* ========== FILTRES ========== */}
      <div className="gm-filters mb-4">
        <Row className="g-3 align-items-center">
          <Col lg={6} md={12}>
            <div className="gm-search-wrapper">
              <i className="bi bi-search gm-search-icon"></i>
              <input
                type="text"
                className="gm-search-input"
                placeholder="Rechercher par nom, description, entité..."
                value={filterText}
                onChange={(e) => { setFilterText(e.target.value); setCurrentPage(1); }}
              />
              {filterText && (
                <button className="gm-search-clear" onClick={() => setFilterText('')}>
                  <i className="bi bi-x-lg"></i>
                </button>
              )}
            </div>
          </Col>
          <Col lg={3} md={6}>
            <select
              className="gm-select"
              value={itemsPerPage}
              onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}
            >
              <option value={5}>5 par page</option>
              <option value={10}>10 par page</option>
              <option value={25}>25 par page</option>
              <option value={50}>50 par page</option>
            </select>
          </Col>
          <Col lg={3} md={6}>
            <button
              className="gm-btn-secondary w-100"
              onClick={fetchGroupes}
              disabled={actionLoading}
            >
              <i className={`bi ${actionLoading ? 'bi-arrow-repeat gm-spin' : 'bi-arrow-clockwise'}`}></i>
              <span>Actualiser</span>
            </button>
          </Col>
        </Row>
      </div>

      {/* ========== TABLE ========== */}
      <div className="gm-table-card">
        {loading ? (
          <div className="gm-loading">
            <div className="gm-loading-spinner"></div>
            <div className="gm-loading-text">Chargement des groupes...</div>
          </div>
        ) : (
          <>
            <div className="table-responsive">
              <Table className="gm-table mb-0">
                <thead>
                  <tr>
                    <th style={{ width: '50px' }}>
                      <div className="gm-checkbox-wrapper">
                        <input
                          type="checkbox"
                          className="gm-checkbox"
                          checked={isAllSelected}
                          ref={input => { if (input) input.indeterminate = isSomeSelected; }}
                          onChange={handleSelectAll}
                        />
                      </div>
                    </th>
                    <SortableHeader field="nom">
                      <i className="bi bi-collection me-1"></i>Nom
                    </SortableHeader>
                    <SortableHeader field="description">
                      <i className="bi bi-file-text me-1"></i>Description
                    </SortableHeader>
                    <SortableHeader field="business_name">
                      <i className="bi bi-building me-1"></i>Entité
                    </SortableHeader>
                    <SortableHeader field="dateCreation">
                      <i className="bi bi-calendar me-1"></i>Créé le
                    </SortableHeader>
                    <th className="text-end" style={{ width: '140px' }}>
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {currentGroups.length > 0 ? (
                    currentGroups.map((groupe) => (
                      <tr key={groupe.id} className="gm-row">
                        <td>
                          <div className="gm-checkbox-wrapper">
                            <input
                              type="checkbox"
                              className="gm-checkbox"
                              checked={selectedGroups.includes(groupe.id)}
                              onChange={() => handleSelectGroup(groupe.id)}
                            />
                          </div>
                        </td>
                        <td>
                          <div className="gm-name-cell">
                            <div
                              className="gm-color-dot"
                              style={{ backgroundColor: `var(--bs-${groupe.couleur})` }}
                            ></div>
                            <span className="gm-name-main">{groupe.nom}</span>
                          </div>
                        </td>
                        <td>
                          <span className="gm-description" title={groupe.description}>
                            {groupe.description}
                          </span>
                        </td>
                        <td>
                          {groupe.business_name ? (
                            <span className="gm-badge gm-badge-secondary">
                              <i className="bi bi-building"></i>
                              {groupe.business_name}
                            </span>
                          ) : (
                            <span className="text-muted">—</span>
                          )}
                        </td>
                        <td>
                          <span className="gm-date-cell">{groupe.dateCreation}</span>
                        </td>
                        <td className="text-end">
                          <div className="gm-actions">
                            <OverlayTrigger placement="top" overlay={<Tooltip>Voir détails</Tooltip>}>
                              <button
                                className="gm-action-btn gm-action-view"
                                onClick={() => handleViewDetails(groupe)}
                                disabled={actionLoading}
                              >
                                <i className="bi bi-eye"></i>
                              </button>
                            </OverlayTrigger>
                            <OverlayTrigger placement="top" overlay={<Tooltip>Modifier</Tooltip>}>
                              <button
                                className="gm-action-btn gm-action-edit"
                                onClick={() => handleEditGroup(groupe)}
                                disabled={actionLoading}
                              >
                                <i className="bi bi-pencil"></i>
                              </button>
                            </OverlayTrigger>
                            <OverlayTrigger placement="top" overlay={<Tooltip>Supprimer</Tooltip>}>
                              <button
                                className="gm-action-btn gm-action-delete"
                                onClick={() => handleDeleteClick(groupe)}
                                disabled={actionLoading}
                              >
                                <i className="bi bi-trash"></i>
                              </button>
                            </OverlayTrigger>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6">
                        <div className="gm-empty-state">
                          <div className="gm-empty-icon">
                            <i className="bi bi-collection"></i>
                          </div>
                          <h5 className="gm-empty-title">Aucun groupe trouvé</h5>
                          <p className="gm-empty-text">
                            {filterText
                              ? "Aucun groupe ne correspond à votre recherche"
                              : "Commencez par créer votre premier groupe"}
                          </p>
                          {filterText ? (
                            <button className="gm-btn-empty-reset" onClick={() => setFilterText('')}>
                              <i className="bi bi-arrow-clockwise me-2"></i>
                              Réinitialiser
                            </button>
                          ) : (
                            <button className="gm-btn-empty-reset" onClick={() => setShowCreateGroupModal(true)}>
                              <i className="bi bi-plus-circle me-2"></i>
                              Créer un groupe
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>

            {/* Pagination */}
            {filteredAndSortedGroups.length > 0 && totalPages > 1 && (
              <div className="gm-pagination">
                <div className="gm-pagination-info">
                  <i className="bi bi-info-circle"></i>
                  Affichage <strong>{startIndex + 1}</strong> à <strong>{Math.min(startIndex + itemsPerPage, filteredAndSortedGroups.length)}</strong> sur <strong>{filteredAndSortedGroups.length}</strong>
                  {selectedGroups.length > 0 && (
                    <span className="gm-pagination-selected">
                      • <strong>{selectedGroups.length}</strong> sélectionné{selectedGroups.length > 1 ? 's' : ''}
                    </span>
                  )}
                </div>
                <div className="gm-pagination-controls">
                  <button
                    className="gm-page-btn"
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage(1)}
                  >
                    <i className="bi bi-chevron-double-left"></i>
                  </button>
                  <button
                    className="gm-page-btn"
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage(currentPage - 1)}
                  >
                    <i className="bi bi-chevron-left"></i>
                  </button>

                  {[...Array(Math.min(5, totalPages))].map((_, index) => {
                    const pageNum = currentPage <= 3
                      ? index + 1
                      : currentPage >= totalPages - 2
                        ? totalPages - 4 + index
                        : currentPage - 2 + index;

                    if (pageNum > 0 && pageNum <= totalPages) {
                      return (
                        <button
                          key={pageNum}
                          className={`gm-page-btn ${currentPage === pageNum ? 'active' : ''}`}
                          onClick={() => setCurrentPage(pageNum)}
                        >
                          {pageNum}
                        </button>
                      );
                    }
                    return null;
                  })}

                  <button
                    className="gm-page-btn"
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage(currentPage + 1)}
                  >
                    <i className="bi bi-chevron-right"></i>
                  </button>
                  <button
                    className="gm-page-btn"
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage(totalPages)}
                  >
                    <i className="bi bi-chevron-double-right"></i>
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* ============================================
          MODAL CRÉATION
          ============================================ */}
      <Modal show={showCreateGroupModal} onHide={() => setShowCreateGroupModal(false)} centered className="gm-modal">
        <div className="gm-modal-header gm-modal-header-success">
          <div className="d-flex align-items-center gap-3">
            <div className="gm-modal-icon gm-modal-icon-success">
              <i className="bi bi-plus-circle-fill"></i>
            </div>
            <div>
              <h5 className="gm-modal-title mb-0">Nouveau groupe</h5>
              <small className="gm-modal-subtitle">Créer un groupe de contacts</small>
            </div>
          </div>
          <button className="gm-modal-close" onClick={() => setShowCreateGroupModal(false)}>
            <i className="bi bi-x-lg"></i>
          </button>
        </div>

        <Modal.Body className="gm-modal-body">
          <div className="gm-form-group">
            <label className="gm-form-label">
              Nom du groupe <span className="gm-required">*</span>
            </label>
            <input
              type="text"
              className="gm-input"
              value={groupForm.name}
              onChange={(e) => setGroupForm({ ...groupForm, name: e.target.value })}
              placeholder="Ex: Clients VIP"
              autoFocus
            />
            <div className="gm-form-hint">
              <i className="bi bi-info-circle"></i>
              Le nom du groupe doit être unique et descriptif
            </div>
          </div>
        </Modal.Body>

        <div className="gm-modal-footer">
          <button className="gm-btn-cancel" onClick={() => setShowCreateGroupModal(false)}>
            Annuler
          </button>
          <button
            className="gm-btn-primary"
            onClick={handleCreateGroup}
            disabled={actionLoading || !groupForm.name.trim()}
          >
            {actionLoading ? (
              <>
                <Spinner animation="border" size="sm" />
                Création...
              </>
            ) : (
              <>
                <i className="bi bi-check-lg"></i>
                Créer le groupe
              </>
            )}
          </button>
        </div>
      </Modal>

      {/* ============================================
          MODAL ÉDITION
          ============================================ */}
      <Modal show={showEditGroupModal} onHide={() => setShowEditGroupModal(false)} centered className="gm-modal">
        <div className="gm-modal-header gm-modal-header-blue">
          <div className="d-flex align-items-center gap-3">
            <div className="gm-modal-icon gm-modal-icon-blue">
              <i className="bi bi-pencil-square"></i>
            </div>
            <div>
              <h5 className="gm-modal-title mb-0">Modifier le groupe</h5>
              <small className="gm-modal-subtitle">{selectedGroup?.nom}</small>
            </div>
          </div>
          <button className="gm-modal-close" onClick={() => setShowEditGroupModal(false)}>
            <i className="bi bi-x-lg"></i>
          </button>
        </div>

        <Modal.Body className="gm-modal-body">
          <div className="gm-form-group">
            <label className="gm-form-label">
              Nom du groupe <span className="gm-required">*</span>
            </label>
            <input
              type="text"
              className="gm-input"
              value={groupForm.name}
              onChange={(e) => setGroupForm({ ...groupForm, name: e.target.value })}
              placeholder="Ex: Clients VIP"
              autoFocus
            />
          </div>
        </Modal.Body>

        <div className="gm-modal-footer">
          <button className="gm-btn-cancel" onClick={() => setShowEditGroupModal(false)}>
            Annuler
          </button>
          <button
            className="gm-btn-primary"
            onClick={handleUpdateGroup}
            disabled={actionLoading || !groupForm.name.trim()}
          >
            {actionLoading ? (
              <>
                <Spinner animation="border" size="sm" />
                Mise à jour...
              </>
            ) : (
              <>
                <i className="bi bi-check-lg"></i>
                Mettre à jour
              </>
            )}
          </button>
        </div>
      </Modal>

      {/* ============================================
          MODAL DÉTAILS
          ============================================ */}
      <Modal show={showDetailModal} onHide={() => setShowDetailModal(false)} centered size="lg" className="gm-modal">
        <div className="gm-modal-header gm-modal-header-info">
          <div className="d-flex align-items-center gap-3">
            <div className="gm-modal-icon gm-modal-icon-info">
              <i className="bi bi-info-circle-fill"></i>
            </div>
            <div>
              <h5 className="gm-modal-title mb-0">Détails du groupe</h5>
              <small className="gm-modal-subtitle">{selectedGroup?.nom}</small>
            </div>
          </div>
          <button className="gm-modal-close" onClick={() => setShowDetailModal(false)}>
            <i className="bi bi-x-lg"></i>
          </button>
        </div>

        <Modal.Body className="gm-modal-body">
          {detailLoading ? (
            <div className="gm-detail-loading">
              <div className="gm-loading-spinner"></div>
              <div className="gm-loading-text">Chargement des détails...</div>
            </div>
          ) : groupDetail ? (
            <>
              {/* HERO avec icône carrée colorée */}
              <div className="gm-detail-hero">
                <div
                  className="gm-detail-hero-icon"
                  style={{
                    backgroundColor: `var(--bs-${selectedGroup?.couleur || 'primary'})`,
                    boxShadow: `0 8px 20px -6px var(--bs-${selectedGroup?.couleur || 'primary'})`
                  }}
                >
                  <i className="bi bi-collection-fill"></i>
                </div>
                <div className="gm-detail-hero-content">
                  <div className="gm-detail-hero-name">{groupDetail.name}</div>
                  <div className="gm-detail-hero-meta">
                    <span>
                      <i className="bi bi-building"></i>
                      {selectedGroup?.business_name || 'Non défini'}
                    </span>
                  </div>
                </div>
                <div className="gm-detail-hero-stat">
                  <div className="gm-detail-hero-stat-value">{selectedGroup?.contactsCount || 0}</div>
                  <div className="gm-detail-hero-stat-label">Contacts</div>
                </div>
              </div>

              {/* GRID */}
              <div className="gm-detail-grid">
                <div className="gm-detail-card">
                  <div className="gm-detail-card-icon gm-detail-card-icon-blue">
                    <i className="bi bi-hash"></i>
                  </div>
                  <div className="gm-detail-card-content">
                    <div className="gm-detail-card-label">ID Groupe</div>
                    <div className="gm-detail-card-value font-mono">#{groupDetail.id}</div>
                  </div>
                </div>

                <div className="gm-detail-card">
                  <div className="gm-detail-card-icon gm-detail-card-icon-violet">
                    <i className="bi bi-person"></i>
                  </div>
                  <div className="gm-detail-card-content">
                    <div className="gm-detail-card-label">User ID</div>
                    <div className="gm-detail-card-value font-mono">#{groupDetail.user_id}</div>
                  </div>
                </div>

                <div className="gm-detail-card">
                  <div className="gm-detail-card-icon gm-detail-card-icon-purple">
                    <i className="bi bi-building"></i>
                  </div>
                  <div className="gm-detail-card-content">
                    <div className="gm-detail-card-label">Entité ID</div>
                    <div className="gm-detail-card-value font-mono">#{groupDetail.entite_id}</div>
                  </div>
                </div>

                <div className="gm-detail-card">
                  <div className="gm-detail-card-icon gm-detail-card-icon-green">
                    <i className="bi bi-calendar-check"></i>
                  </div>
                  <div className="gm-detail-card-content">
                    <div className="gm-detail-card-label">Créé le</div>
                    <div className="gm-detail-card-value">
                      {new Date(groupDetail.created_at).toLocaleDateString('fr-FR', {
                        day: '2-digit', month: '2-digit', year: 'numeric'
                      })}
                    </div>
                  </div>
                </div>

                <div className="gm-detail-card">
                  <div className="gm-detail-card-icon gm-detail-card-icon-amber">
                    <i className="bi bi-arrow-repeat"></i>
                  </div>
                  <div className="gm-detail-card-content">
                    <div className="gm-detail-card-label">Modifié le</div>
                    <div className="gm-detail-card-value">
                      {new Date(groupDetail.updated_at).toLocaleDateString('fr-FR', {
                        day: '2-digit', month: '2-digit', year: 'numeric'
                      })}
                    </div>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="gm-detail-empty">
              <i className="bi bi-exclamation-triangle"></i>
              <p>Impossible de charger les détails</p>
            </div>
          )}
        </Modal.Body>

        <div className="gm-modal-footer">
          <button className="gm-btn-cancel" onClick={() => setShowDetailModal(false)}>
            Fermer
          </button>
          {groupDetail && (
            <button
              className="gm-btn-primary"
              onClick={() => {
                setShowDetailModal(false);
                handleEditGroup(selectedGroup);
              }}
            >
              <i className="bi bi-pencil"></i>
              Modifier
            </button>
          )}
        </div>
      </Modal>

      {/* ============================================
          MODAL SUPPRESSION
          ============================================ */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered className="gm-modal">
        <Modal.Body className="gm-delete-body">
          <div className="gm-delete-icon-wrapper">
            <div className="gm-delete-icon">
              <i className="bi bi-trash"></i>
            </div>
          </div>
          <h4 className="gm-delete-title">
            {selectedGroups.length > 0
              ? `Supprimer ${selectedGroups.length} groupe(s) ?`
              : `Supprimer ce groupe ?`
            }
          </h4>
          <p className="gm-delete-text">
            {selectedGroups.length > 0
              ? "Vous êtes sur le point de supprimer plusieurs groupes."
              : `Vous êtes sur le point de supprimer "${selectedGroup?.nom}".`
            }
          </p>

          {/* ✅ Suppression individuelle avec icône carrée colorée */}
          {selectedGroup && selectedGroups.length === 0 && (
            <div className="gm-delete-group">
              <div
                className="gm-delete-icon-square"
                style={{
                  backgroundColor: `var(--bs-${selectedGroup.couleur || 'primary'})`,
                  boxShadow: `0 6px 14px -4px var(--bs-${selectedGroup.couleur || 'primary'})`
                }}
              >
                <i className="bi bi-collection-fill"></i>
              </div>
              <div className="gm-delete-info">
                <div className="gm-delete-name">{selectedGroup.nom}</div>
                <div className="gm-delete-meta">
                  <span><i className="bi bi-people"></i> {selectedGroup.contactsCount || 0} contacts</span>
                  <span><i className="bi bi-building"></i> {selectedGroup.business_name}</span>
                </div>
              </div>
            </div>
          )}

          {/* ✅ Suppression multiple avec icône gradient grise */}
          {selectedGroups.length > 1 && (
            <div className="gm-delete-group gm-delete-group-multi">
              <div className="gm-delete-icon-square gm-delete-icon-multi">
                <i className="bi bi-collection-fill"></i>
              </div>
              <div className="gm-delete-info">
                <div className="gm-delete-name">{selectedGroups.length} groupes sélectionnés</div>
                <div className="gm-delete-meta">
                  <span>Cette action ne peut pas être annulée</span>
                </div>
              </div>
            </div>
          )}

          <div className="gm-delete-warning">
            <i className="bi bi-exclamation-triangle"></i>
            Cette action est irréversible
          </div>

          <div className="gm-delete-actions">
            <button
              className="gm-btn-cancel gm-btn-flex"
              onClick={() => setShowDeleteModal(false)}
              disabled={actionLoading}
            >
              <i className="bi bi-x-lg"></i>
              Annuler
            </button>
            <button
              className="gm-btn-danger gm-btn-flex"
              onClick={selectedGroups.length > 0 ? handleConfirmBulkDelete : handleConfirmDelete}
              disabled={actionLoading}
            >
              {actionLoading ? (
                <>
                  <Spinner animation="border" size="sm" />
                  Suppression...
                </>
              ) : (
                <>
                  <i className="bi bi-trash"></i>
                  Supprimer
                </>
              )}
            </button>
          </div>
        </Modal.Body>
      </Modal>

      <Footer />

      {/* ========== STYLES PREMIUM ========== */}
      <style>{`
        /* ========== GLOBAL ========== */
        .gm-page {
          background: #f8fafc;
          min-height: 100vh;
        }
        .gm-page * {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        }

        /* ========== HEADER ========== */
        .gm-header {
          background: linear-gradient(135deg, #ffffff 0%, #f1f5f9 100%);
          border-radius: 16px;
          padding: 24px 28px;
          border: 1px solid #e2e8f0;
          box-shadow: 0 1px 3px rgba(0,0,0,0.03);
        }
        .gm-header-icon {
          width: 56px;
          height: 56px;
          border-radius: 14px;
          background: linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%);
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 26px;
          box-shadow: 0 8px 20px -6px rgba(59, 130, 246, 0.5);
        }
        .gm-header-title {
          font-weight: 700;
          color: #0f172a;
          font-size: 1.5rem;
          letter-spacing: -0.02em;
        }
        .gm-header-subtitle {
          color: #64748b;
          font-size: 0.875rem;
        }

        /* ========== BUTTONS ========== */
        .gm-btn-primary, .gm-btn-secondary, .gm-btn-cancel, .gm-btn-danger {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          padding: 11px 20px;
          border-radius: 10px;
          font-weight: 600;
          font-size: 0.875rem;
          border: none;
          transition: all 0.2s;
          cursor: pointer;
          text-decoration: none;
        }
        .gm-btn-primary {
          background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
          color: white;
        }
        .gm-btn-primary:hover:not(:disabled) {
          background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
          transform: translateY(-1px);
          box-shadow: 0 8px 16px -4px rgba(59, 130, 246, 0.4);
        }
        .gm-btn-primary:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        .gm-btn-secondary {
          background: white;
          border: 1px solid #e2e8f0;
          color: #334155;
        }
        .gm-btn-secondary:hover:not(:disabled) {
          background: #f8fafc;
          border-color: #cbd5e1;
          color: #0f172a;
        }
        .gm-btn-secondary:disabled {
          opacity: 0.6;
        }
        .gm-btn-danger {
          background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
          color: white;
        }
        .gm-btn-danger:hover:not(:disabled) {
          background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
          transform: translateY(-1px);
          box-shadow: 0 8px 16px -4px rgba(239, 68, 68, 0.4);
        }
        .gm-btn-cancel {
          background: white;
          border: 1px solid #e2e8f0;
          color: #334155;
        }
        .gm-btn-cancel:hover {
          background: #f1f5f9;
        }
        .gm-btn-flex {
          flex: 1;
        }
        .gm-dropdown-menu {
          border: 1px solid #e2e8f0;
          border-radius: 10px;
          box-shadow: 0 8px 24px -8px rgba(0,0,0,0.1);
          padding: 6px;
        }
        .gm-dropdown-menu .dropdown-item {
          border-radius: 6px;
          padding: 8px 12px;
          font-size: 0.85rem;
          display: flex;
          align-items: center;
        }
        .gm-dropdown-menu .dropdown-item:hover {
          background: #f1f5f9;
        }

        /* ========== ALERTS ========== */
        .gm-alert {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 14px 18px;
          border-radius: 12px;
          font-size: 0.85rem;
          font-weight: 500;
        }
        .gm-alert > i {
          font-size: 1.3rem;
          flex-shrink: 0;
        }
        .gm-alert-danger {
          background: linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%);
          color: #991b1b;
          border-left: 4px solid #ef4444;
        }

        /* ========== STATS ========== */
        .gm-stat-card {
          display: flex;
          align-items: center;
          gap: 14px;
          background: white;
          border-radius: 14px;
          padding: 18px 20px;
          border: 1px solid #e2e8f0;
          transition: all 0.25s;
        }
        .gm-stat-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 20px -6px rgba(0,0,0,0.08);
        }
        .gm-stat-icon-wrapper {
          width: 46px;
          height: 46px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 20px;
          flex-shrink: 0;
        }
        .gm-stat-icon-blue   { background: #eff6ff; color: #3b82f6; }
        .gm-stat-icon-green  { background: #d1fae5; color: #10b981; }
        .gm-stat-icon-violet { background: #ede9fe; color: #8b5cf6; }
        .gm-stat-content {
          min-width: 0;
          flex-grow: 1;
        }
        .gm-stat-label {
          font-size: 0.72rem;
          color: #64748b;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          margin-bottom: 3px;
        }
        .gm-stat-value {
          font-size: 1.5rem;
          font-weight: 700;
          color: #0f172a;
          letter-spacing: -0.02em;
          line-height: 1;
        }

        /* ========== FILTRES ========== */
        .gm-filters {
          background: white;
          border-radius: 14px;
          padding: 16px 20px;
          border: 1px solid #e2e8f0;
        }
        .gm-search-wrapper {
          position: relative;
          display: flex;
          align-items: center;
        }
        .gm-search-icon {
          position: absolute;
          left: 14px;
          color: #94a3b8;
          font-size: 1.05rem;
          pointer-events: none;
        }
        .gm-search-input {
          width: 100%;
          padding: 11px 40px 11px 42px;
          border: 1px solid #e2e8f0;
          border-radius: 10px;
          font-size: 0.875rem;
          background: #f8fafc;
          transition: all 0.2s;
          outline: none;
          font-family: inherit;
        }
        .gm-search-input:focus {
          background: white;
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }
        .gm-search-clear {
          position: absolute;
          right: 8px;
          background: none;
          border: none;
          color: #94a3b8;
          font-size: 1rem;
          padding: 6px 10px;
          cursor: pointer;
          border-radius: 6px;
          transition: all 0.15s;
        }
        .gm-search-clear:hover {
          background: #fee2e2;
          color: #ef4444;
        }
        .gm-select {
          width: 100%;
          padding: 11px 14px;
          border: 1px solid #e2e8f0;
          border-radius: 10px;
          font-size: 0.875rem;
          font-weight: 500;
          background: #f8fafc;
          color: #334155;
          outline: none;
          cursor: pointer;
          font-family: inherit;
        }
        .gm-select:focus {
          background: white;
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }

        /* ========== TABLE ========== */
        .gm-table-card {
          background: white;
          border-radius: 16px;
          border: 1px solid #e2e8f0;
          overflow: hidden;
          box-shadow: 0 1px 3px rgba(0,0,0,0.03);
        }
        .gm-table {
          font-size: 0.875rem;
        }
        .gm-table thead th {
          background: #f8fafc;
          color: #475569;
          font-weight: 700;
          font-size: 0.7rem;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          padding: 16px;
          border-bottom: 1px solid #e2e8f0;
          border-top: none;
          white-space: nowrap;
        }
        .gm-table tbody td {
          padding: 16px;
          vertical-align: middle;
          border-bottom: 1px solid #f1f5f9;
        }
        .gm-row {
          transition: background-color 0.15s;
        }
        .gm-row:hover {
          background-color: #f8fafc;
        }
        .gm-sortable-header {
          cursor: pointer;
          user-select: none;
        }
        .gm-sortable-header:hover {
          color: #3b82f6;
        }
        .gm-sortable-content {
          display: inline-flex;
          align-items: center;
          gap: 6px;
        }
        .gm-sort-icon {
          color: #3b82f6;
          font-size: 0.75rem;
        }

        .gm-checkbox-wrapper {
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .gm-checkbox {
          width: 18px;
          height: 18px;
          border-radius: 5px;
          border: 1.5px solid #cbd5e1;
          cursor: pointer;
          accent-color: #3b82f6;
        }

        .gm-name-cell {
          display: flex;
          align-items: center;
          gap: 10px;
        }
        .gm-color-dot {
          width: 12px;
          height: 12px;
          border-radius: 50%;
          flex-shrink: 0;
          box-shadow: 0 0 0 3px rgba(0,0,0,0.05);
        }
        .gm-name-main {
          font-weight: 700;
          color: #0f172a;
        }

        .gm-description {
          color: #64748b;
          font-size: 0.82rem;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
          max-width: 300px;
        }

        .gm-date-cell {
          color: #64748b;
          font-size: 0.8rem;
          font-variant-numeric: tabular-nums;
        }

        .gm-badge {
          display: inline-flex;
          align-items: center;
          gap: 5px;
          padding: 4px 10px;
          border-radius: 6px;
          font-size: 0.72rem;
          font-weight: 600;
          white-space: nowrap;
        }
        .gm-badge-secondary {
          background: #f1f5f9;
          color: #475569;
          border: 1px solid #e2e8f0;
        }

        .gm-actions {
          display: inline-flex;
          gap: 6px;
          justify-content: flex-end;
        }
        .gm-action-btn {
          width: 34px;
          height: 34px;
          border-radius: 8px;
          border: 1px solid #e2e8f0;
          background: white;
          color: #64748b;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.15s;
          font-size: 0.9rem;
        }
        .gm-action-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        .gm-action-view:hover:not(:disabled) {
          background: #06b6d4;
          border-color: #06b6d4;
          color: white;
          transform: translateY(-1px);
          box-shadow: 0 4px 12px -2px rgba(6, 182, 212, 0.4);
        }
        .gm-action-edit:hover:not(:disabled) {
          background: #3b82f6;
          border-color: #3b82f6;
          color: white;
          transform: translateY(-1px);
          box-shadow: 0 4px 12px -2px rgba(59, 130, 246, 0.4);
        }
        .gm-action-delete:hover:not(:disabled) {
          background: #ef4444;
          border-color: #ef4444;
          color: white;
          transform: translateY(-1px);
          box-shadow: 0 4px 12px -2px rgba(239, 68, 68, 0.4);
        }

        /* ========== LOADING / EMPTY ========== */
        .gm-loading {
          padding: 80px 20px;
          text-align: center;
        }
        .gm-loading-spinner {
          width: 48px;
          height: 48px;
          border: 4px solid #e2e8f0;
          border-top-color: #3b82f6;
          border-radius: 50%;
          animation: gm-spin 0.8s linear infinite;
          margin: 0 auto 16px;
        }
        .gm-loading-text {
          color: #64748b;
          font-size: 0.875rem;
          font-weight: 500;
        }
        .gm-empty-state {
          padding: 60px 20px;
          text-align: center;
        }
        .gm-empty-icon {
          width: 80px;
          height: 80px;
          border-radius: 50%;
          background: #f1f5f9;
          color: #94a3b8;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: 36px;
          margin-bottom: 20px;
        }
        .gm-empty-title {
          color: #334155;
          font-weight: 700;
          margin-bottom: 8px;
        }
        .gm-empty-text {
          color: #94a3b8;
          margin-bottom: 20px;
        }
        .gm-btn-empty-reset {
          display: inline-flex;
          align-items: center;
          background: white;
          color: #3b82f6;
          border: 1px solid #bfdbfe;
          border-radius: 8px;
          font-weight: 600;
          padding: 8px 16px;
          cursor: pointer;
          transition: all 0.2s;
        }
        .gm-btn-empty-reset:hover {
          background: #3b82f6;
          color: white;
          border-color: #3b82f6;
        }

        /* ========== PAGINATION ========== */
        .gm-pagination {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 16px 24px;
          background: #f8fafc;
          border-top: 1px solid #e2e8f0;
          flex-wrap: wrap;
          gap: 12px;
        }
        .gm-pagination-info {
          font-size: 0.8rem;
          color: #64748b;
          display: flex;
          align-items: center;
          gap: 6px;
        }
        .gm-pagination-info strong {
          color: #0f172a;
        }
        .gm-pagination-selected {
          color: #3b82f6;
          margin-left: 8px;
        }
        .gm-pagination-controls {
          display: flex;
          align-items: center;
          gap: 6px;
        }
        .gm-page-btn {
          min-width: 34px;
          height: 34px;
          padding: 0 10px;
          border-radius: 8px;
          border: 1px solid #e2e8f0;
          background: white;
          color: #475569;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.15s;
          font-size: 0.8rem;
          font-weight: 600;
        }
        .gm-page-btn:hover:not(:disabled) {
          background: #3b82f6;
          color: white;
          border-color: #3b82f6;
        }
        .gm-page-btn:disabled {
          opacity: 0.4;
          cursor: not-allowed;
        }
        .gm-page-btn.active {
          background: #3b82f6;
          color: white;
          border-color: #3b82f6;
        }

        /* ========== MODAL ========== */
        .gm-modal .modal-content {
          border: none;
          border-radius: 16px;
          overflow: hidden;
        }
        .gm-modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 20px 24px;
          color: white;
        }
        .gm-modal-header-success {
          background: linear-gradient(135deg, #10b981 0%, #059669 100%);
        }
        .gm-modal-header-blue {
          background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
        }
        .gm-modal-header-info {
          background: linear-gradient(135deg, #06b6d4 0%, #0891b2 100%);
        }
        .gm-modal-icon {
          width: 44px;
          height: 44px;
          border-radius: 10px;
          background: rgba(255,255,255,0.2);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 20px;
        }
        .gm-modal-title {
          font-weight: 700;
          letter-spacing: -0.01em;
          margin: 0;
        }
        .gm-modal-subtitle {
          color: rgba(255,255,255,0.85);
          font-size: 0.8rem;
        }
        .gm-modal-close {
          background: rgba(255,255,255,0.1);
          border: none;
          color: white;
          width: 36px;
          height: 36px;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.2s;
          font-size: 1rem;
        }
        .gm-modal-close:hover {
          background: rgba(255,255,255,0.2);
          transform: rotate(90deg);
        }
        .gm-modal-body {
          padding: 24px;
          background: #f8fafc;
          max-height: 65vh;
          overflow-y: auto;
        }
        .gm-modal-footer {
          padding: 16px 24px;
          background: white;
          border-top: 1px solid #e2e8f0;
          display: flex;
          justify-content: flex-end;
          gap: 10px;
        }

        /* Form */
        .gm-form-group {
          margin-bottom: 4px;
        }
        .gm-form-label {
          display: block;
          font-weight: 700;
          color: #0f172a;
          font-size: 0.82rem;
          margin-bottom: 8px;
        }
        .gm-required {
          color: #ef4444;
        }
        .gm-input {
          width: 100%;
          padding: 11px 14px;
          border: 2px solid #e2e8f0;
          border-radius: 10px;
          font-size: 0.875rem;
          color: #0f172a;
          background: white;
          transition: all 0.2s;
          outline: none;
          font-family: inherit;
        }
        .gm-input:focus {
          border-color: #3b82f6;
          box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.1);
        }
        .gm-form-hint {
          margin-top: 8px;
          font-size: 0.75rem;
          color: #94a3b8;
          display: flex;
          align-items: center;
          gap: 5px;
        }
        .gm-form-hint i {
          color: #3b82f6;
        }

        /* Detail Hero avec icône carrée */
        .gm-detail-hero {
          display: flex;
          align-items: center;
          gap: 16px;
          padding: 20px;
          background: white;
          border-radius: 14px;
          border: 1px solid #e2e8f0;
          margin-bottom: 20px;
          flex-wrap: wrap;
        }
        .gm-detail-hero-icon {
          width: 64px;
          height: 64px;
          border-radius: 16px;
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 28px;
          flex-shrink: 0;
          transition: all 0.3s ease;
        }
        .gm-detail-hero-icon:hover {
          transform: scale(1.05) rotate(-5deg);
        }
        .gm-detail-hero-content {
          flex-grow: 1;
          min-width: 0;
        }
        .gm-detail-hero-name {
          font-weight: 700;
          color: #0f172a;
          font-size: 1.15rem;
          margin-bottom: 6px;
        }
        .gm-detail-hero-meta {
          display: flex;
          flex-wrap: wrap;
          gap: 14px;
          font-size: 0.82rem;
          color: #64748b;
        }
        .gm-detail-hero-meta span {
          display: inline-flex;
          align-items: center;
          gap: 5px;
        }
        .gm-detail-hero-meta i {
          color: #94a3b8;
        }
        .gm-detail-hero-stat {
          text-align: center;
          padding: 8px 20px;
          background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
          border-radius: 12px;
          border: 1px solid #bfdbfe;
          flex-shrink: 0;
        }
        .gm-detail-hero-stat-value {
          font-size: 1.5rem;
          font-weight: 700;
          color: #2563eb;
          line-height: 1;
        }
        .gm-detail-hero-stat-label {
          font-size: 0.7rem;
          color: #64748b;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          margin-top: 4px;
        }

        /* Detail Grid */
        .gm-detail-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 12px;
        }
        .gm-detail-card {
          display: flex;
          align-items: center;
          gap: 14px;
          padding: 16px;
          background: white;
          border-radius: 12px;
          border: 1px solid #e2e8f0;
          transition: all 0.2s;
        }
        .gm-detail-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 16px -6px rgba(0, 0, 0, 0.08);
        }
        .gm-detail-card-icon {
          width: 42px;
          height: 42px;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 18px;
          flex-shrink: 0;
        }
        .gm-detail-card-icon-blue   { background: #eff6ff; color: #3b82f6; }
        .gm-detail-card-icon-violet { background: #ede9fe; color: #8b5cf6; }
        .gm-detail-card-icon-purple { background: #f5f3ff; color: #7c3aed; }
        .gm-detail-card-icon-green  { background: #d1fae5; color: #059669; }
        .gm-detail-card-icon-amber  { background: #fef3c7; color: #d97706; }
        .gm-detail-card-content {
          flex-grow: 1;
          min-width: 0;
        }
        .gm-detail-card-label {
          font-size: 0.68rem;
          color: #94a3b8;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          margin-bottom: 4px;
        }
        .gm-detail-card-value {
          font-weight: 700;
          color: #0f172a;
          font-size: 0.9rem;
        }
        .gm-detail-card-value.font-mono {
          font-family: 'SF Mono', Menlo, Monaco, monospace;
          font-size: 0.82rem;
        }

        .gm-detail-loading {
          padding: 60px 20px;
          text-align: center;
        }
        .gm-detail-empty {
          padding: 60px 20px;
          text-align: center;
          color: #94a3b8;
        }
        .gm-detail-empty > i {
          font-size: 3rem;
          opacity: 0.5;
          display: block;
          margin-bottom: 12px;
        }

        /* Delete modal */
        .gm-delete-body {
          padding: 32px 24px 24px;
          text-align: center;
        }
        .gm-delete-icon-wrapper {
          margin-bottom: 20px;
        }
        .gm-delete-icon {
          width: 80px;
          height: 80px;
          border-radius: 50%;
          background: linear-gradient(135deg, #fee2e2 0%, #fecaca 100%);
          color: #dc2626;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: 36px;
          box-shadow: 0 12px 24px -8px rgba(220, 38, 38, 0.3);
        }
        .gm-delete-title {
          font-weight: 700;
          color: #0f172a;
          margin-bottom: 12px;
          font-size: 1.25rem;
        }
        .gm-delete-text {
          color: #64748b;
          margin-bottom: 20px;
          font-size: 0.875rem;
        }
        .gm-delete-group {
          display: flex;
          align-items: center;
          gap: 14px;
          background: #f8fafc;
          border-radius: 12px;
          padding: 16px 20px;
          margin-bottom: 16px;
          border: 1px solid #e2e8f0;
          text-align: left;
        }
        .gm-delete-group-multi {
          justify-content: center;
        }
        .gm-delete-icon-square {
          width: 56px;
          height: 56px;
          border-radius: 14px;
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 24px;
          flex-shrink: 0;
          transition: all 0.3s ease;
        }
        .gm-delete-icon-square:hover {
          transform: scale(1.05) rotate(-5deg);
        }
        .gm-delete-icon-multi {
          background: linear-gradient(135deg, #94a3b8 0%, #64748b 100%);
          box-shadow: 0 6px 14px -4px rgba(148, 163, 184, 0.5);
        }
        .gm-delete-info {
          min-width: 0;
          flex-grow: 1;
        }
        .gm-delete-name {
          font-weight: 700;
          color: #0f172a;
          font-size: 0.95rem;
          margin-bottom: 4px;
        }
        .gm-delete-meta {
          display: flex;
          gap: 12px;
          flex-wrap: wrap;
          font-size: 0.78rem;
          color: #94a3b8;
        }
        .gm-delete-meta span {
          display: inline-flex;
          align-items: center;
          gap: 4px;
        }
        .gm-delete-warning {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 8px 14px;
          background: #fef2f2;
          color: #dc2626;
          border-radius: 8px;
          font-size: 0.78rem;
          font-weight: 700;
          margin-bottom: 20px;
        }
        .gm-delete-actions {
          display: flex;
          gap: 10px;
        }

        /* ========== ANIMATIONS ========== */
        .gm-spin {
          animation: gm-spin 1s linear infinite;
          display: inline-block;
        }
        @keyframes gm-spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        /* ========== RESPONSIVE ========== */
        @media (max-width: 992px) {
          .gm-detail-grid {
            grid-template-columns: 1fr;
          }
        }
        @media (max-width: 768px) {
          .gm-header {
            padding: 20px;
          }
          .gm-header-title {
            font-size: 1.2rem;
          }
          .gm-pagination {
            flex-direction: column;
          }
          .gm-stat-value {
            font-size: 1.25rem;
          }
          .gm-modal-body {
            padding: 16px;
          }
          .gm-delete-actions {
            flex-direction: column;
          }
          .gm-detail-hero {
            padding: 16px;
          }
        }
      `}</style>
    </div>
  );
}